import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';

import { confirmSiteDirectionsToken, getSiteDirectionsTokenForUser } from '@/api';
import { getUserId, wentRight } from '@/helpers';

type UseEventConfirm = (args: { id: string; skip: boolean }) => {
  loading: boolean;
  loadingConfirm: boolean;
  canConfirm: boolean;
  onConfirm: () => void;
};

const useEventConfirm: UseEventConfirm = ({ id, skip }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const token = searchParams.get('token');
  const { data, loading } = useQuery(getSiteDirectionsTokenForUser, {
    variables: { input: { userId: getUserId()!, popupEventVendorId: id } },
    skip,
  });
  const isDashboard = location.pathname.includes('/dashboard');

  const [confirm, { loading: loadingConfirm }] = useMutation(confirmSiteDirectionsToken, {
    refetchQueries: isDashboard
      ? ['GetTodoTasksForVendors', 'GetConfirmedEvents', 'GetUpcomingPopupEventDetails']
      : ['GetCalendarEvents', 'GetCalendarEventsList', 'GetUpcomingPopupEventDetails'],
  });

  const digest = data?.getSiteDirectionsTokenForUser?.siteDirectionsToken?.digest ?? '';

  const onSuccessConfirm = async () => {
    if (isDashboard) {
      wentRight(
        'The event will appear in Upcoming Events section in a few seconds',
        'Event confirmed'
      );
    } else {
      wentRight('', 'Event confirmed');
    }
  };
  const onConfirm = async () => {
    if (!digest) {
      return;
    }

    await confirm({ variables: { digest } });
    onSuccessConfirm();
    if (token) {
      searchParams.delete('token');
      setSearchParams(searchParams);
    }
  };

  useEffect(() => {
    if (token && digest) {
      onConfirm();
    }
  }, [token, digest]);

  return {
    loading: loading,
    canConfirm: !!digest,
    onConfirm,
    loadingConfirm,
  };
};

export default useEventConfirm;
